import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { sessionsApi, getDocumentStatus, getCourseDocs } from '../api/sessions.api'
import { useSessionStore } from '../store/sessionStore'

export function useSessionMessages(sessionId: string) {
  return useQuery({
    queryKey: ['sessions', sessionId, 'messages'],
    queryFn: () => sessionsApi.getMessages(sessionId).then((r) => r.data.data),
    enabled: !!sessionId,
  })
}

export function useSession(sessionId: string) {
  return useQuery({
    queryKey: ['sessions', sessionId],
    queryFn: () => sessionsApi.get(sessionId).then((r) => r.data.data),
    enabled: !!sessionId,
  })
}

export function useStartSession() {
  const { setCurrentSession } = useSessionStore()
  const navigate = useNavigate()
  return useMutation({
    mutationFn: (courseId: string) =>
      sessionsApi.start(courseId).then((r) => r.data.data),
    onSuccess: (session) => {
      setCurrentSession(session)
      navigate(`/courses/${session.courseId}/sessions/${session.id}`)
    },
  })
}

export function usePauseSession() {
  const { setCurrentSession } = useSessionStore()
  return useMutation({
    mutationFn: (sessionId: string) =>
      sessionsApi.pause(sessionId).then((r) => r.data.data),
    onSuccess: (session) => setCurrentSession(session),
  })
}

export function useResumeSession() {
  const { setCurrentSession } = useSessionStore()
  return useMutation({
    mutationFn: (sessionId: string) =>
      sessionsApi.resume(sessionId).then((r) => r.data.data),
    onSuccess: (session) => setCurrentSession(session),
  })
}

export function useEndSession() {
  const { setCurrentSession } = useSessionStore()
  return useMutation({
    mutationFn: (sessionId: string) =>
      sessionsApi.end(sessionId).then((r) => r.data.data),
    onSuccess: (session) => setCurrentSession(session),
  })
}

export function useDocumentStatus(courseId: string, documentId: string | null) {
  return useQuery({
    queryKey: ['document-status', courseId, documentId],
    queryFn: () => getDocumentStatus(courseId, documentId!),
    enabled: !!documentId && !!courseId,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'ready' || status === 'failed' ? false : 3000
    },
  })
}

export function useCourseDocuments(courseId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['course-documents', courseId],
    queryFn: () => getCourseDocs(courseId),
    enabled: enabled && !!courseId,
    retry: false,
  })
}
